import { useCallback, useEffect, useRef } from "react";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useCommands } from "@components/commands/useCommands";
import { useBulkActions } from "@components/transactions/TransactionsBulkActions/useBulkActions";
import { useAccrualReviewTaskFocus } from "./AccrualReviewTaskFocusContext";

/**
 * Lets Enter keep activating a focused button, link or menu item natively,
 * and leaves it to any open menu or to the dialog it was pressed in (the
 * match drawer, schedule modals), so it never acts on the row behind them.
 */
export const isEnterReservedByFocusedControl = (event: KeyboardEvent) =>
    hasOpenMenu() ||
    (event.target instanceof Element &&
        event.target.closest(
            "button, a, [role='button'], [role='menuitem'], [role='dialog']",
        ) !== null);

interface UseAccrualTaskRowActionsParams {
    selectedCount: number;
    /** A bulk operation is running; M and L wait for it, as the bar's buttons do. */
    isBusy: boolean;
    openBulkMemo: () => void;
    openBulkAccrualLedger: () => void;
    openBulkActions: () => void;
}

/**
 * Memo, Ledger and Actions for the accrual task: the bar shows them for the
 * selection. With nothing selected the bar hides them, and their keys act on
 * the focused row instead: M opens its memo, L its accrual ledger, mod+K its
 * Actions, and Enter confirms it.
 */
export const useAccrualTaskRowActions = ({
    selectedCount,
    isBusy,
    openBulkMemo,
    openBulkAccrualLedger,
    openBulkActions,
}: UseAccrualTaskRowActionsParams) => {
    const { focusedTransaction, openFocusedCellAction } =
        useAccrualReviewTaskFocus();
    const { select } = useBulkActions();
    const { isViewOnly } = useViewOnlyRefusal();
    const { registerOpenOverride } = useCommands();

    const hasSelection = selectedCount > 0;
    const isFocusedRowTarget = !hasSelection && focusedTransaction !== null;
    const shouldOpenActionsOnSelectRef = useRef(false);

    const handleMemo = useCallback(() => {
        if (hasSelection) {
            openBulkMemo();
            return;
        }
        openFocusedCellAction(TransactionColumn.NOTE);
    }, [hasSelection, openBulkMemo, openFocusedCellAction]);

    const handleLedger = useCallback(() => {
        if (hasSelection) {
            openBulkAccrualLedger();
            return;
        }
        openFocusedCellAction(TransactionColumn.ACCRUAL_REVIEW_LEDGER);
    }, [hasSelection, openBulkAccrualLedger, openFocusedCellAction]);

    const handleActions = useCallback(() => {
        if (hasSelection) {
            openBulkActions();
            return;
        }
        if (!focusedTransaction) {
            return;
        }
        shouldOpenActionsOnSelectRef.current = true;
        select(focusedTransaction);
    }, [hasSelection, openBulkActions, focusedTransaction, select]);

    // The bulk list reads its options from the selection, so it opens on
    // the render where selecting the focused row has landed.
    useEffect(() => {
        if (!shouldOpenActionsOnSelectRef.current || !hasSelection) {
            return;
        }
        shouldOpenActionsOnSelectRef.current = false;
        openBulkActions();
    }, [hasSelection, openBulkActions]);

    const handleActionsRef = useLatestRef(handleActions);
    useEffect(() => {
        if (!isFocusedRowTarget) {
            return;
        }
        return registerOpenOverride(() => handleActionsRef.current());
    }, [isFocusedRowTarget, registerOpenOverride, handleActionsRef]);

    const isFocusedRowEditable = isFocusedRowTarget && !isViewOnly;
    const canEditFocusedRowCell = isFocusedRowEditable && !isBusy;

    useKeyboardCommand({
        keys: "m",
        callback: handleMemo,
        options: {
            enabled: canEditFocusedRowCell,
            description: "Update memo for focused row",
            preventDefault: true,
        },
    });

    useKeyboardCommand({
        keys: "l",
        callback: handleLedger,
        options: {
            enabled: canEditFocusedRowCell,
            description: "Update accrual ledger for focused row",
            preventDefault: true,
        },
    });

    useKeyboardCommand({
        keys: "enter",
        callback: () =>
            openFocusedCellAction(TransactionColumn.ACCRUAL_ACTIONS),
        options: {
            enabled: isFocusedRowEditable,
            description: "Confirm or review focused row",
            preventDefault: true,
            ignoreEventWhen: isEnterReservedByFocusedControl,
        },
    });

    return {
        handleMemo,
        handleLedger,
        handleActions,
    };
};
